import React, { useState } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Register #15: read-only report - counts and field names only, never a coordinate
export const PhotoLocationMetadataCard = ({ token }) => {
  const [rep, setRep] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try { const r = await axios.get(`${API_URL}/api/admin/photos/location-metadata/latest`, { headers: { Authorization: `Bearer ${token}` } }); setRep(r.data.latest); } catch (e) { /* non-blocking */ }
  };
  const run = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API_URL}/api/admin/photos/location-metadata/scan`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setRep(r.data); toast.success('Scan started — read-only, nothing changes');
      const poll = setInterval(async () => {
        try {
          const l = await axios.get(`${API_URL}/api/admin/photos/location-metadata/latest`, { headers: { Authorization: `Bearer ${token}` } });
          setRep(l.data.latest);
          if (l.data.latest?.status !== 'running') { clearInterval(poll); setBusy(false); }
        } catch (e) { clearInterval(poll); setBusy(false); }
      }, 3000);
      return;
    } catch (e) { toast.error(e.response?.data?.detail || 'Scan failed'); } finally { setBusy(false); }
  };
  React.useEffect(() => { load(); /* eslint-disable-next-line */ }, []);
  const c = rep?.counts;
  return (
    <div className="rounded-2xl border border-border/60 p-5 mb-6" data-testid="photo-location-metadata-card">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h3 className="font-bold text-lg">Photo location metadata (register #15)</h3>
          <p className="text-xs text-muted-foreground">Stored originals inspected read-only; every delivered copy (diary, admin view, download, AI copy) is stripped of EXIF/GPS/XMP/IPTC. Counts and field names only — no coordinate is ever read into this report.</p>
        </div>
        <Button onClick={run} disabled={busy} size="sm" className="rounded-full" data-testid="photo-metadata-scan-btn">{busy ? 'Scanning…' : 'Scan stored photos'}</Button>
      </div>
      {rep ? (
        <div className="mt-3 text-sm" data-testid="photo-metadata-report">
          <p className="font-mono text-xs" data-testid="photo-metadata-status">{String(rep.at).slice(0, 19)}Z · {rep.status || 'completed'} · stored photos {rep.total_stored_photos} · scanned {c.scanned}{rep.candidates ? ` of ${rep.candidates}` : ''} · fetch failed {c.fetch_failed}</p>
          <p className="mt-1"><span className={`font-bold ${c.with_gps ? 'text-red-700' : 'text-green-700'}`} data-testid="photo-metadata-gps-count">{c.with_gps} with GPS</span> · {c.with_exif} with EXIF · {c.with_xmp} with XMP · {c.with_iptc} with IPTC · {c.clean} clean</p>
          {rep.gps_field_names && Object.keys(rep.gps_field_names).length > 0 && <p className="text-xs mt-1">GPS field names: {Object.entries(rep.gps_field_names).map(([k, v]) => `${k} (${v})`).join(', ')}</p>}
          {rep.exif_field_names && Object.keys(rep.exif_field_names).length > 0 && <p className="text-xs mt-1 text-muted-foreground">EXIF field names: {Object.entries(rep.exif_field_names).slice(0, 25).map(([k, v]) => `${k} (${v})`).join(', ')}</p>}
          {rep.entries_with_gps?.length > 0 && <p className="text-xs mt-1 font-mono">entries with GPS (id prefixes): {rep.entries_with_gps.join(', ')}</p>}
          <p className="text-[11px] text-muted-foreground mt-1">Compares: {rep.compares}</p>
        </div>
      ) : <p className="text-sm text-muted-foreground mt-2" data-testid="photo-metadata-none">No scan yet.</p>}
    </div>
  );
};
